import React, { useMemo, useState } from 'react'
import {
  Box,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Step,
  StepLabel,
  Stepper,
  Tooltip,
} from '@mui/material'
import { DateCalendar, LocalizationProvider, PickersDay, PickersDayProps } from '@mui/x-date-pickers'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import moment, { Moment } from 'moment'
import { requestLookback } from 'api'
import { reportException } from 'tools/logs'
import { LookbackCoverage } from 'interfaces/ISummaryReport'
import strings from 'strings'
import { Modal, Toast as toast, TypoGraph } from 'components'

const m = strings.SUMMARY_REPORT.DATA_COVERAGE.MODAL

const WarningIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    aria-hidden="true"
  >
    <path d="M12 9v4" />
    <path d="M12 17h.01" />
    <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />
  </svg>
)

interface ILookbackModal {
  coverage: LookbackCoverage
  // The same ones `coverage` was loaded with: re-reading localStorage could target another carrier.
  carrierId: string
  codeType: string
  codeValue: string
  onClose: () => void
  onRequested: () => void
}

const LookbackModal: React.FC<ILookbackModal> = (props) => {
  const { coverage, carrierId, codeType, codeValue, onClose, onRequested } = props
  // Absent means the fleet was never read, so it shows a dash rather than a zero it did not measure.
  const vehicles = typeof coverage.vehicleCount === 'number' ? coverage.vehicleCount : null
  const [step, setStep] = useState(0)
  const [startDate, setStartDate] = useState<Moment | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const lastSelectable = moment(coverage.coverageStart).subtract(1, 'day')
  // Fixed, never picked: any earlier end would leave a gap between what is retrieved and what the report already has.
  const endDate = lastSelectable
  const firstSelectable = moment(coverage.earliestSelectableDate ?? undefined)

  const billedMonths = useMemo(() => {
    if (!startDate || !endDate) {
      return []
    }
    const months: string[] = []
    const cursor = startDate.clone().startOf('month')
    while (cursor.isSameOrBefore(endDate, 'month')) {
      months.push(cursor.format('MMMM YYYY'))
      cursor.add(1, 'month')
    }
    return months
  }, [startDate, endDate])

  const days = startDate && endDate ? endDate.diff(startDate, 'days') + 1 : 0

  // Only the ends: a year of history would otherwise list twelve month names on one line.
  const monthsBilledLabel = billedMonths.length
    ? `${
        billedMonths.length > 1
          ? `${billedMonths[0]} – ${billedMonths[billedMonths.length - 1]}`
          : billedMonths[0]
      } — ${billedMonths.length}${billedMonths.length === 1 ? m.FULL_MONTH : m.FULL_MONTHS}`
    : '-'



  const handleConfirm = () => {
    if (!startDate || !endDate) {
      return
    }
    setSubmitting(true)
    requestLookback(
      carrierId,
      codeType,
      codeValue,
      startDate.format('YYYY-MM-DD'),
      endDate.format('YYYY-MM-DD')
    )
      .then(() => {
        toast({ id: 'lookbackRequested', type: 'success', subTitle: m.SUCCESS })
        onRequested()
      })
      .catch((error) => {
        reportException(error)
        toast({ id: 'lookbackFailed', type: 'error', subTitle: m.FAILURE })
      })
      .finally(() => setSubmitting(false))
  }

  const callout = (content: React.ReactNode) => (
    <Box
      sx={{
        display: 'flex',
        gap: 1.5,
        mt: 2,
        p: '14px 16px',
        borderRadius: '6px',
        backgroundColor: 'rgba(245,181,41,.1)',
        border: '1px solid rgba(245,181,41,.5)',
        borderLeftWidth: '3px',
        '& .MuiTypography-root': { fontSize: 13 },
      }}
    >
      <Box sx={{ flex: 'none', color: '#a8760a', display: 'flex' }}>
        <WarningIcon />
      </Box>
      <Box>{content}</Box>
    </Box>
  )

  const reviewBody = (
    <DialogContent>
      <TypoGraph variant="body2">
        {`${m.REVIEW_INTRO_PRE}${moment(coverage.coverageStart).format('MMMM YYYY')}${m.REVIEW_INTRO_POST}${m.REVIEW_INTRO_TAIL}`}
      </TypoGraph>
      {callout(
        <TypoGraph variant="body2">
          <b>{m.DISCLAIMER_PAID}</b> {m.DISCLAIMER_BILLING}
        </TypoGraph>
      )}
      <TypoGraph variant="body2" color="text.secondary" sx={{ mt: 2 }}>
        {`${m.DISCLAIMER_AVAILABILITY} ${m.DISCLAIMER_PREVIEW}`}
      </TypoGraph>
    </DialogContent>
  )

  /** Explains on the day itself why it cannot be moved, rather than leaving a calendar that silently ignores clicks. */
  const fixedDay = (dayProps: PickersDayProps<Moment>) => {
    const day = <PickersDay {...dayProps} />
    return dayProps.selected ? (
      <Tooltip title={m.END_FIXED_HINT} arrow describeChild>
        {day}
      </Tooltip>
    ) : (
      day
    )
  }

  const calendar = (
    label: string,
    value: Moment | null,
    min: Moment,
    max: Moment,
    onPick: (picked: Moment | null) => void,
    testId: string,
    readOnly = false
  ) => (
    <Box data-testid={testId}>
      <TypoGraph variant="caption" color="text.secondary" sx={{ display: 'block', pl: 1 }}>
        {label}
      </TypoGraph>
      <DateCalendar
        value={value}
        minDate={min}
        maxDate={max}
        onChange={onPick}
        readOnly={readOnly}
        slots={readOnly ? { day: fixedDay } : undefined}
        sx={{
          width: 300,
          '& .MuiPickersDay-root.Mui-disabled:not(.MuiPickersDay-dayOutsideMonth)': {
            textDecoration: 'line-through',
          },
        }}
      />
    </Box>
  )

  const costLine = (label: string, value: string, bold = false, hint?: string) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.5 }}>
      <TypoGraph
        variant="body2"
        color="text.secondary"
        sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {label}
        {hint && (
          <Tooltip title={hint} arrow>
            {/* Focusable so the explanation is not mouse-only. */}
            <Box
              component="span"
              tabIndex={0}
              aria-label={hint}
              data-testid="vehicles-included-hint"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 15,
                height: 15,
                borderRadius: '50%',
                border: '1px solid',
                borderColor: 'text.disabled',
                color: 'text.secondary',
                fontSize: 10,
                lineHeight: 1,
                cursor: 'help',
              }}>
              ?
            </Box>
          </Tooltip>
        )}
      </TypoGraph>
      <TypoGraph variant="body2" sx={{ fontWeight: bold ? 700 : 400, textAlign: 'right' }}>
        {value}
      </TypoGraph>
    </Box>
  )

  const legendItem = (color: string, label: string) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
      <Box
        sx={{
          width: 11,
          height: 11,
          borderRadius: '50%',
          backgroundColor: color,
          border: '1px solid',
          borderColor: 'grey.300',
        }}
      />
      <TypoGraph variant="caption" color="text.secondary">
        {label}
      </TypoGraph>
    </Box>
  )

  const datesBody = (
    <DialogContent>
      <LocalizationProvider dateAdapter={AdapterMoment}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
          {calendar(
            m.FROM,
            startDate,
            firstSelectable,
            endDate ?? lastSelectable,
            setStartDate,
            'lookback-calendar-start'
          )}
          {calendar(
            m.TO,
            endDate,
            startDate ?? firstSelectable,
            lastSelectable,
            () => undefined,
            'lookback-calendar-end',
            true
          )}
        </Box>
      </LocalizationProvider>

      <Box sx={{ display: 'flex', gap: 2.5, flexWrap: 'wrap', mt: 1 }}>
        {legendItem('primary.main', m.SELECTED)}
        {legendItem('common.white', m.AVAILABLE)}
        {legendItem('grey.400', m.ALREADY_IN_REPORT)}
      </Box>

      {!days && (
        <TypoGraph variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          {m.NO_SELECTION}
        </TypoGraph>
      )}

      <Divider sx={{ my: 2 }} />

      <Box>
        {costLine(
          m.DAYS_SELECTED,
          days
            ? `${days}${days === 1 ? m.DAY : m.DAYS} · ${startDate?.format('MM/DD')} – ${endDate?.format('MM/DD/YYYY')}`
            : '-'
        )}
        {costLine(m.MONTHS_BILLED, monthsBilledLabel)}
        {costLine(m.VEHICLES, vehicles != null ? String(vehicles) : '-', false, m.VEHICLES_HINT)}
        {costLine(
          m.BILLABLE,
          vehicles != null && billedMonths.length ? `${vehicles * billedMonths.length}${m.VEHICLE_MONTHS}` : '-',
          true
        )}
      </Box>
      <TypoGraph variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
        {m.COST_NOTE}
      </TypoGraph>

      {callout(
        <>
          <TypoGraph variant="body2">
            <b>{m.SCOPE_TITLE}</b> {m.SCOPE_INCLUDED} {m.SCOPE_INTEGRATION}
          </TypoGraph>
          <TypoGraph variant="body2" sx={{ mt: 1 }}>
            {m.SCOPE_EXCLUDED}
          </TypoGraph>
        </>
      )}
    </DialogContent>
  )

  const steps = (
    <Stepper activeStep={step} sx={{ px: 3, pt: 2 }}>
      <Step>
        <StepLabel>{m.STEP_REVIEW}</StepLabel>
      </Step>
      <Step>
        <StepLabel>{m.STEP_DATES}</StepLabel>
      </Step>
    </Stepper>
  )

  return (
    <Modal
      open
      size="md"
      fullWidth={false}
      handleClose={onClose}
      dismissOnClose={!submitting}
      dom={
        <Box sx={{ width: step === 0 ? 560 : 700, maxWidth: '100%' }}>
          {steps}
          <DialogTitle sx={{ pb: 0 }}>
            {step === 0 ? m.REVIEW_TITLE : m.DATES_TITLE}
          </DialogTitle>
          {step === 0 ? reviewBody : datesBody}
          <DialogActions sx={{ borderTop: 1, borderColor: 'divider', px: 3, py: 2 }}>
            <Button
              variant="contained"
              disableElevation
              data-testid="secondary"
              disabled={submitting}
              onClick={step === 0 ? onClose : () => setStep(0)}
              sx={{
                backgroundColor: 'grey.200',
                color: 'text.primary',
                '&:hover': { backgroundColor: 'grey.300' },
              }}
            >
              {step === 0 ? m.CANCEL : m.BACK}
            </Button>
            <Button
              variant="contained"
              color="primary"
              data-testid="primary"
              disabled={submitting || (step === 1 && !startDate)}
              onClick={step === 0 ? () => setStep(1) : handleConfirm}
              startIcon={submitting ? <CircularProgress size={18} /> : null}
            >
              {step === 0 ? m.NEXT : m.CONFIRM}
            </Button>
          </DialogActions>
        </Box>
      }
    />
  )
}

export default LookbackModal
